/**
 * Centralized query keys. Using factory functions keeps invalidation
 * predictable and avoids typos in scattered string literals.
 */
export const queryKeys = {
	notifications: {
		all: ['notifications'] as const,
		list: (filter: 'all' | 'unread') =>
			[...queryKeys.notifications.all, 'list', filter] as const,
		unreadCount: () =>
			[...queryKeys.notifications.all, 'unread-count'] as const,
		preferences: () => [...queryKeys.notifications.all, 'preferences'] as const,
	},
	dashboard: {
		all: ['dashboard'] as const,
		summary: () => [...queryKeys.dashboard.all, 'summary'] as const,
		offlineDisplays: () =>
			[...queryKeys.dashboard.all, 'offline-displays'] as const,
		offAirDisplays: () =>
			[...queryKeys.dashboard.all, 'off-air-displays'] as const,
		customerPlays: (rangeDays: number) =>
			[...queryKeys.dashboard.all, 'customer-plays', rangeDays] as const,
		recentErrors: () => [...queryKeys.dashboard.all, 'recent-errors'] as const,
	},
	auth: {
		all: ['auth'] as const,
		profile: () => [...queryKeys.auth.all, 'profile'] as const,
		mfaStatus: () => [...queryKeys.auth.all, 'mfa-status'] as const,
	},
	displays: {
		all: ['displays'] as const,
		/** Infinite list, scoped by the exact query/filter combination. */
		list: (query: Record<string, unknown>) =>
			[...queryKeys.displays.all, 'list', query] as const,
		detail: (id: string) => [...queryKeys.displays.all, 'detail', id] as const,
		/** Lazy tree nodes, scoped by subtree root + depth (+ viewed tenant). */
		groupHierarchy: (
			rootId: string | undefined,
			maxDepth: number,
			tenantId?: string,
		) =>
			[
				...queryKeys.displays.all,
				'hierarchy',
				rootId ?? 'root',
				maxDepth,
				tenantId ?? 'own',
			] as const,
		groupDetail: (id: string) =>
			[...queryKeys.displays.all, 'group', id] as const,
	},
	media: {
		all: ['media'] as const,
		/** Direct children of a folder/root, scoped by the exact filter set. */
		list: (query: Record<string, unknown>) =>
			[...queryKeys.media.all, 'list', query] as const,
		/** Folder-only list used by the navigation column. */
		folders: (query: Record<string, unknown>) =>
			[...queryKeys.media.all, 'folders', query] as const,
		/** Lazy folder subtree, scoped by root + depth (+ viewed tenant). */
		tree: (rootId: string | undefined, maxDepth: number, tenantId?: string) =>
			[
				...queryKeys.media.all,
				'tree',
				rootId ?? 'root',
				maxDepth,
				tenantId ?? 'own',
			] as const,
		detail: (id: string) => [...queryKeys.media.all, 'detail', id] as const,
		breadcrumbs: (id: string) =>
			[...queryKeys.media.all, 'breadcrumbs', id] as const,
		thumbnailUrl: (scope: string, id: string) =>
			[...queryKeys.media.all, 'thumbnail-url', scope, id] as const,
		downloadUrl: (scope: string, id: string) =>
			[...queryKeys.media.all, 'download-url', scope, id] as const,
		attachmentUrl: (scope: string, id: string) =>
			[...queryKeys.media.all, 'attachment-url', scope, id] as const,
		/** Probed video duration, cached forever per media. */
		videoDuration: (scope: string, id: string) =>
			[...queryKeys.media.all, 'video-duration', scope, id] as const,
	},
	uploads: {
		all: ['uploads'] as const,
		/** Scoped by auth context so cache never leaks across user/tenant. */
		status: (scope: string, uploadId: string) =>
			[...queryKeys.uploads.all, scope, uploadId] as const,
		/** One-shot metadata read for previews. */
		meta: (scope: string, uploadId: string) =>
			[...queryKeys.uploads.all, 'meta', scope, uploadId] as const,
		/** Short-lived signed URL; never cached beyond the component lifetime. */
		downloadUrl: (scope: string, uploadId: string) =>
			[...queryKeys.uploads.all, 'download-url', scope, uploadId] as const,
	},
	customers: {
		all: ['customers'] as const,
		/** Infinite list, scoped by the exact filter combination. */
		list: (query: Record<string, unknown>) =>
			[...queryKeys.customers.all, 'list', query] as const,
		detail: (id: string) => [...queryKeys.customers.all, 'detail', id] as const,
		documents: (id: string) =>
			[...queryKeys.customers.all, 'documents', id] as const,
		/** Short, active-only list used by the media customer picker. */
		options: (tenantId?: string) =>
			[...queryKeys.customers.all, 'options', tenantId ?? 'own'] as const,
		/** Short list of every customer (any status) for list filters. */
		filterOptions: (tenantId?: string) =>
			[
				...queryKeys.customers.all,
				'filter-options',
				tenantId ?? 'own',
			] as const,
	},
	playlists: {
		all: ['playlists'] as const,
		list: (query: Record<string, unknown>) =>
			[...queryKeys.playlists.all, 'list', query] as const,
		detail: (id: string) => [...queryKeys.playlists.all, 'detail', id] as const,
	},
	schedules: {
		all: ['schedules'] as const,
		list: (query: Record<string, unknown>) =>
			[...queryKeys.schedules.all, 'list', query] as const,
		detail: (id: string) => [...queryKeys.schedules.all, 'detail', id] as const,
		preview: (id: string, at: string) =>
			[...queryKeys.schedules.all, 'preview', id, at] as const,
		upcoming: (id: string) =>
			[...queryKeys.schedules.all, 'upcoming', id] as const,
	},
	audit: {
		all: ['audit'] as const,
		list: (query: Record<string, unknown>) =>
			[...queryKeys.audit.all, 'list', query] as const,
		summary: (query: Record<string, unknown>) =>
			[...queryKeys.audit.all, 'summary', query] as const,
		authList: (query: Record<string, unknown>) =>
			[...queryKeys.audit.all, 'auth', query] as const,
		activity: (query: Record<string, unknown>) =>
			[...queryKeys.audit.all, 'activity', query] as const,
		performance: (displayId: string, minutes: number) =>
			[...queryKeys.audit.all, 'performance', displayId, minutes] as const,
	},
	reports: {
		all: ['reports'] as const,
		list: (query: Record<string, unknown>) =>
			[...queryKeys.reports.all, 'list', query] as const,
		/** Server-computed range limits; `earliestFrom` moves with time. */
		limits: () => [...queryKeys.reports.all, 'limits'] as const,
		estimate: (body: Record<string, unknown>) =>
			[...queryKeys.reports.all, 'estimate', body] as const,
	},
	/** Customer portal (separate session); cleared on portal sign-out. */
	portal: {
		all: ['portal'] as const,
		me: () => [...queryKeys.portal.all, 'me'] as const,
		summary: (range: Record<string, unknown>) =>
			[...queryKeys.portal.all, 'summary', range] as const,
		playsDaily: (range: Record<string, unknown>) =>
			[...queryKeys.portal.all, 'plays-daily', range] as const,
		mediaPlays: (query: Record<string, unknown>) =>
			[...queryKeys.portal.all, 'media-plays', query] as const,
		displayPlays: (query: Record<string, unknown>) =>
			[...queryKeys.portal.all, 'display-plays', query] as const,
		reports: (query: Record<string, unknown>) =>
			[...queryKeys.portal.all, 'reports', 'list', query] as const,
		reportEstimate: (body: Record<string, unknown>) =>
			[...queryKeys.portal.all, 'reports', 'estimate', body] as const,
		invitation: (token: string) =>
			[...queryKeys.portal.all, 'invitation', token] as const,
	},
	/** Portal accounts of one customer (CMS admin view). */
	portalAccounts: {
		all: ['portal-accounts'] as const,
		/** Infinite list, scoped by the exact filter combination. */
		list: (query: Record<string, unknown>) =>
			[...queryKeys.portalAccounts.all, 'list', query] as const,
	},
	users: {
		all: ['users'] as const,
		/** One page of the user list, scoped by tenant and exact filters. */
		list: (tenantId: string, query: Record<string, unknown>) =>
			[...queryKeys.users.all, tenantId, 'list', query] as const,
		detail: (tenantId: string, id: string) =>
			[...queryKeys.users.all, tenantId, 'detail', id] as const,
	},
	tenants: {
		all: ['tenants'] as const,
		list: () => [...queryKeys.tenants.all, 'list'] as const,
	},
	/** Tenant seats. `own` = the caller's tenant. */
	seats: {
		all: ['seats'] as const,
		summary: (tenantId: string) =>
			[...queryKeys.seats.all, 'summary', tenantId] as const,
		summaries: () => [...queryKeys.seats.all, 'summaries'] as const,
		codes: (query: Record<string, unknown>) =>
			[...queryKeys.seats.all, 'codes', query] as const,
		adjustments: (tenantId: string, page: number) =>
			[...queryKeys.seats.all, 'adjustments', tenantId, page] as const,
	},
	/** On-prem: deployment mode, seat sync of an install, on-prem tenants. */
	onprem: {
		all: ['onprem'] as const,
		deployment: () => [...queryKeys.onprem.all, 'deployment'] as const,
		syncStatus: () => [...queryKeys.onprem.all, 'sync-status'] as const,
		tenants: () => [...queryKeys.onprem.all, 'tenants'] as const,
	},
	/** Kiosk exit code page (its own sign-in, not the CMS session). */
	kioskAccess: {
		all: ['kiosk-access'] as const,
		code: (displayId: string) =>
			[...queryKeys.kioskAccess.all, 'code', displayId] as const,
		challengeCode: (displayId: string, challenge: string, version: number) =>
			[
				...queryKeys.kioskAccess.all,
				'challenge-code',
				displayId,
				challenge,
				version,
			] as const,
	},
	/** Canva integration of the signed-in user. */
	canva: {
		all: ['canva'] as const,
		connection: () => [...queryKeys.canva.all, 'connection'] as const,
		designs: (query: Record<string, unknown>) =>
			[...queryKeys.canva.all, 'designs', query] as const,
		import: (id: string) => [...queryKeys.canva.all, 'import', id] as const,
	},
	/**
	 * Device app updates. `own` = caller's tenant (platform admins may pick
	 * another one).
	 */
	appUpdate: {
		all: ['app-update'] as const,
		releases: () => [...queryKeys.appUpdate.all, 'releases'] as const,
		releasePage: (params: { page: number; limit: number; search?: string }) =>
			[...queryKeys.appUpdate.all, 'release-page', params] as const,
		globalPolicy: () =>
			[...queryKeys.appUpdate.all, 'policy', 'global'] as const,
		tenantPolicy: (tenantId: string) =>
			[...queryKeys.appUpdate.all, 'policy', 'tenant', tenantId] as const,
		displays: (
			tenantId: string,
			params: {
				page?: number;
				limit?: number;
				filter?: string;
				search?: string;
				groupId?: string;
			} = {},
		) => [...queryKeys.appUpdate.all, 'displays', tenantId, params] as const,
		displaySummary: (tenantId: string) =>
			[...queryKeys.appUpdate.all, 'display-summary', tenantId] as const,
		display: (displayId: string) =>
			[...queryKeys.appUpdate.all, 'display', displayId] as const,
	},
	/** Vendor SDK modules (platform-wide, platform admins). */
	vendorModules: {
		all: ['vendor-modules'] as const,
		list: () => [...queryKeys.vendorModules.all, 'list'] as const,
	},
	/** Workspace security settings (CMS IP allowlist). `own` = caller's tenant. */
	security: {
		all: ['security'] as const,
		ipAllowlist: (tenantId: string) =>
			[...queryKeys.security.all, 'ip-allowlist', tenantId] as const,
		ipAllowlistSelf: (tenantId: string) =>
			[...queryKeys.security.all, 'ip-allowlist-self', tenantId] as const,
	},
	invitations: {
		all: ['invitations'] as const,
		/** Public invitation lookup by its token. */
		detail: (token: string) =>
			[...queryKeys.invitations.all, 'detail', token] as const,
	},
	displaySnapshots: {
		all: ['display-snapshots'] as const,
		/** Everything of one display: invalidated after each mutation. */
		display: (displayId: string) =>
			[...queryKeys.displaySnapshots.all, displayId] as const,
		list: (displayId: string, page: number) =>
			[...queryKeys.displaySnapshots.display(displayId), 'list', page] as const,
		/** Signed thumbnail URL; refetched whenever it may have expired. */
		thumbnail: (displayId: string, snapshotId: string) =>
			[
				...queryKeys.displaySnapshots.display(displayId),
				'thumbnail',
				snapshotId,
			] as const,
	},
	deviceControl: {
		all: ['device-control'] as const,
		/** Everything of one display: invalidated after each mutation. */
		display: (displayId: string) =>
			[...queryKeys.deviceControl.all, displayId] as const,
		state: (displayId: string) =>
			[...queryKeys.deviceControl.display(displayId), 'state'] as const,
		commands: (displayId: string, page: number) =>
			[
				...queryKeys.deviceControl.display(displayId),
				'commands',
				page,
			] as const,
		/** Kiosk secret versions and the device clock (no code). */
		kiosk: (displayId: string) =>
			[...queryKeys.deviceControl.display(displayId), 'kiosk'] as const,
		/** The current kiosk exit code, only while it is shown. */
		kioskCode: (displayId: string) =>
			[...queryKeys.deviceControl.display(displayId), 'kiosk-code'] as const,
		/** Device overview of a group and its sub-groups (one page). */
		group: (groupId: string, page: number) =>
			[...queryKeys.deviceControl.all, 'group', groupId, page] as const,
	},
	floorPlans: {
		all: ['floor-plans'] as const,
		buildings: (query: Record<string, unknown>, tenantId?: string) =>
			[
				...queryKeys.floorPlans.all,
				'buildings',
				tenantId ?? 'own',
				query,
			] as const,
		building: (id: string, tenantId?: string) =>
			[...queryKeys.floorPlans.all, 'building', id, tenantId ?? 'own'] as const,
		floors: (buildingId: string, tenantId?: string) =>
			[
				...queryKeys.floorPlans.all,
				'floors',
				buildingId,
				tenantId ?? 'own',
			] as const,
		/** A floor's plan: image URL and placed displays. */
		plan: (floorId: string, tenantId?: string) =>
			[
				...queryKeys.floorPlans.all,
				'plan',
				floorId,
				tenantId ?? 'own',
			] as const,
		/** Approved displays without a position (the drag list). */
		unplaced: (query: Record<string, unknown>) =>
			[...queryKeys.floorPlans.all, 'unplaced', query] as const,
	},
	powerPlans: {
		all: ['power-plans'] as const,
		/** One page of the plan list, scoped by the exact query. */
		list: (query: Record<string, unknown>) =>
			[...queryKeys.powerPlans.all, 'list', query] as const,
		detail: (id: string) =>
			[...queryKeys.powerPlans.all, 'detail', id] as const,
		displays: (id: string, page: number) =>
			[...queryKeys.powerPlans.all, 'displays', id, page] as const,
		/** Display search of the assign dialog. */
		displaySearch: (search: string) =>
			[...queryKeys.powerPlans.all, 'display-search', search] as const,
	},
	playbacks: {
		all: ['playbacks'] as const,
		list: (query: Record<string, unknown>) =>
			[...queryKeys.playbacks.all, 'list', query] as const,
		current: (displayId: string) =>
			[...queryKeys.playbacks.all, 'current', displayId] as const,
		/** Now-playing summaries of one table page (sorted ids). */
		currentSummaries: (displayIds: readonly string[]) =>
			[
				...queryKeys.playbacks.all,
				'current-summaries',
				[...displayIds].sort(),
			] as const,
		preparation: (groupId: string) =>
			[...queryKeys.playbacks.all, 'preparation', groupId] as const,
		upcoming: (displayId: string) =>
			[...queryKeys.playbacks.all, 'upcoming', displayId] as const,
		playingDisplays: (query: Record<string, unknown>) =>
			[...queryKeys.playbacks.all, 'playing-displays', query] as const,
	},
} satisfies Record<string, Record<string, unknown>>;
